"use client"
import type { ComponentProps, ReactNode } from "react"
import { Banner } from "@cloudflare/kumo/components/banner"
import { Loader } from "@cloudflare/kumo/components/loader"
import { Button } from "@cloudflare/kumo/components/button"
import { CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningIcon, XIcon } from "@phosphor-icons/react"
const noticeVariant = { error: "error", warning: "alert", success: "default", info: "default" } as const
const noticeIcon = {
  error: <WarningCircleIcon weight="fill" />,
  warning: <WarningIcon weight="fill" />,
  success: <CheckCircleIcon weight="fill" />,
  info: <InfoIcon weight="fill" />,
}

/**
 * A page-level message in Kumo's structured banner. Place it above the cards it
 * concerns, never inside a LayerCard; a problem with one row or value is a
 * status and a sentence beside that row instead.
 */
export function Notice({
  tone = "info",
  title,
  description,
  action,
  onCloseButtonClick,
  ...props
}: Omit<ComponentProps<"div">, "title"> & {
  tone?: "error" | "success" | "warning" | "info"
  title: string
  description?: ReactNode
  action?: ReactNode
  onCloseButtonClick?: () => void
}) {
  const close = onCloseButtonClick ? (
    <Button variant="ghost" shape="square" size="sm" icon={XIcon} aria-label="Dismiss notification" onClick={onCloseButtonClick} />
  ) : null
  return (
    <div {...props} role={tone === "error" ? "alert" : "status"}>
      <Banner
        variant={noticeVariant[tone]}
        icon={noticeIcon[tone]}
        title={title}
        description={description}
        action={action || close ? <>{action}{close}</> : undefined}
      />
    </div>
  )
}
export function LoadingState({ description }: { description: string }) {
  return (
    <div role="status" className="flex items-center gap-3 py-6 text-kumo-subtle">
      <Loader />
      {description}
    </div>
  )
}
